import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ChevronRight, User, MapPin, CalendarOff, Plus, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { cn } from '@/lib/utils';

const SITE_LABELS = {
  'shihab_romeed': 'SR',
  'specialist_dental_london': 'SDL',
  'london_dental_academy': 'LDA'
};

function formatSitesSummary(sites) {
  if (!sites || sites.length === 0) return 'No sites';
  return sites.map(s => SITE_LABELS[s] || s).join(', ');
}

export default function ScheduleIndex() {
  const { apiFetch } = useAdmin();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/providers');
        const data = await res.json();
        setProviders(data.providers || data || []);
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiFetch]);

  const filtered = providers.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Schedule</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">
            Provider Schedules
          </h2>
          <p className="text-stone-500 text-sm mt-1">Manage weekly hours and calendar overrides for each provider.</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
        <Input
          placeholder="Search providers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-white border-stone-200 h-10"
        />
      </div>

      {/* Provider Cards */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="py-16 text-center">
            <Clock className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-600 font-medium">No providers found</p>
            <p className="text-stone-400 text-sm mt-1">
              {search ? 'Try a different search term' : 'Providers will appear here once added'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((provider, i) => (
            <motion.div
              key={provider.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
            >
              <Link to={`/admin/schedule/${provider.id}`}>
                <Card className="border-stone-200 shadow-sm hover:shadow-md hover:border-stone-300 transition-all duration-200 cursor-pointer group">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-stone-800 to-stone-900 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                          {(provider.name || 'P').charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900 group-hover:text-stone-700 transition-colors">
                            {provider.name}
                          </p>
                          {provider.specialties && (
                            <p className="text-xs text-stone-400 mt-0.5">
                              {Array.isArray(provider.specialties) ? provider.specialties.join(', ') : provider.specialties}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-stone-500 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                    </div>

                    <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <MapPin className="w-3.5 h-3.5 text-stone-400" />
                        <span>{formatSitesSummary(provider.sites)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <Clock className="w-3.5 h-3.5 text-stone-400" />
                        <span>Configure →</span>
                      </div>
                    </div>

                    {provider.isActive === false && (
                      <Badge variant="outline" className="mt-3 text-[10px] text-amber-700 border-amber-200 bg-amber-50">
                        Inactive
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
